'use client';

import { useId, useState } from 'react';

import { forgotPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';

type SendStatus = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * Figma „Card · Zabudnuté heslo“ (207:11374): 1 px #E2E5DF card, radius 16, padding 24, 8 px rows –
 * „Nepamätáte si aktuálne heslo?“ (Body/L Bold), the text with the account e-mail (Body/M #363D36)
 * and „Poslať odkaz na obnovenie“ (UI/Button M #0E6A20, underlined). The action is the core
 * forgotten-password request for the account e-mail; the outcome is the core message.
 */
export function ForgotPasswordCard({ email }: { email: string }) {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  const locale = useLocaleStore(s => s.currentLocale);
  const titleId = useId();
  const [status, setStatus] = useState<SendStatus>('idle');
  if (!texts.forgotTitle || !texts.sendLink) return null;

  const send = async () => {
    setStatus('sending');
    try {
      const returnUrl = new URL(ROUTES.RESET_PASSWORD(), env.NEXT_PUBLIC_APP_URL).toString();
      await forgotPassword({ email, return_url: returnUrl, locale });
      setStatus('sent');
    } catch {
      setStatus('failed');
    }
  };

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-2 rounded-2xl border border-[#E2E5DF] bg-white p-4 sm:p-6"
    >
      <h2 id={titleId} className="nt-body-l-bold text-[#0D110D]">
        {texts.forgotTitle}
      </h2>
      {texts.forgotText ? (
        <p className="nt-body-m break-words text-[#363D36]">
          {fillTemplate(texts.forgotText, { email })}
        </p>
      ) : null}
      <button
        type="button"
        onClick={send}
        disabled={status === 'sending' || status === 'sent'}
        aria-busy={status === 'sending' || undefined}
        className={cn(
          'nt-button-m self-start rounded text-left text-[#0E6A20] underline underline-offset-2 transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none disabled:text-[#8F978E] disabled:no-underline',
          status === 'sending' && 'cursor-wait',
        )}
      >
        {texts.sendLink}
      </button>
      <div role="status" className="empty:-mt-2">
        {status === 'sent' || status === 'failed' ? (
          <p
            className={cn(
              'nt-body-m flex items-start gap-1.5',
              status === 'sent' ? 'text-[#0E6A20]' : 'text-[#B42318]',
            )}
          >
            <Icon
              name={status === 'sent' ? 'check-circle' : 'warning-circle'}
              size={18}
              className={cn('mt-px', status === 'sent' ? 'text-[#127A26]' : 'text-[#D92D20]')}
            />
            {status === 'sent' ? t('text652') : t('text651')}
          </p>
        ) : null}
      </div>
    </section>
  );
}
